import { Box, Button, Flex, Heading, Paragraph, Text } from '@rover/kibble/core';
import NewServiceBadge from '@rover/react-lib/src/pages/grooming/components/NewServiceBadge';
import type { CatalogIcon } from '@rover/react-lib/src/pages/shared/serviceCatalog/types';
import getImageSrc from '@rover/shared/js/utils/getImageSrc';

import { ServiceLinkClickEvent } from '../../utilities/analyticsEvents';

import ServiceIcon from './ServiceIcon';

interface ServiceCardProps {
  name: string;
  location: string | null;
  for: string;
  description: string;
  cta: string;
  imageUrl?: string;
  serviceType: string;
  icon?: CatalogIcon;
  href: string;
}

const ServiceCard = ({
  name,
  location,
  for: forText,
  description,
  cta,
  imageUrl,
  serviceType,
  icon,
  href,
}: ServiceCardProps): JSX.Element => {
  const isGrooming = serviceType === 'grooming';

  const handleServiceClick = (): void => {
    new ServiceLinkClickEvent({ serviceType }).fireCrossPageEvent();
  };

  const resolvedImageUrl = getImageSrc(imageUrl);

  return (
    <Flex
      flexDirection={['column', 'row']}
      paddingTop={['0x', '2x']}
      marginBottom={['0x', '8x']}
      sx={{
        overflow: 'hidden',
        gap: ['0x', '16x'],
      }}
    >
      <Flex alignItems="center" gap="2x" marginBottom="5x" display={['flex', 'none']}>
        {icon && <ServiceIcon icon={icon} />}
        <Text fontWeight="semibold" size="300">
          {name}
        </Text>
        {isGrooming && <NewServiceBadge />}
      </Flex>
      <Box
        width={['100%', '50%']}
        height={['140px', 'auto']}
        mb={['4x', '0x']}
        borderRadius="24px"
        position="relative"
        backgroundImage={resolvedImageUrl ? `url(${resolvedImageUrl})` : undefined}
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundRepeat="no-repeat"
        sx={{
          overflow: 'hidden',
        }}
      />
      <Flex
        width={['100%', '50%']}
        flexDirection="column"
        justifyContent="space-between"
        paddingBottom="1x"
      >
        <Box>
          <Box display={['none', 'block']}>
            <Flex alignItems="center" gap="2x" marginBottom="3x">
              {icon && <ServiceIcon icon={icon} />}
              <Heading size="300" marginBottom="0x">
                {name}
              </Heading>
              {isGrooming && <NewServiceBadge />}
            </Flex>
          </Box>
          <Flex alignItems="center" gap="2x" flexWrap="wrap" marginBottom="5x">
            {location && (
              <>
                <Paragraph
                  size="200"
                  webProps={{ maxWidth: 'none' }}
                  textColor="tertiary"
                  marginBottom="0x"
                  fontWeight="semibold"
                >
                  {location}
                </Paragraph>
                <Paragraph
                  size="200"
                  webProps={{ maxWidth: 'none' }}
                  textColor="tertiary"
                  marginBottom="0x"
                >
                  •
                </Paragraph>
              </>
            )}
            <Paragraph
              size="200"
              webProps={{ maxWidth: 'none' }}
              textColor="tertiary"
              marginBottom="0x"
              fontWeight="semibold"
            >
              {forText}
            </Paragraph>
          </Flex>
          <Paragraph size="200" webProps={{ maxWidth: 'none' }} marginBottom="6x">
            {description}
          </Paragraph>
        </Box>
        <Box padding={['1x', '0x']}>
          {href ? (
            <Button href={href} width={['100%', 0.8]} onPress={handleServiceClick}>
              {cta}
            </Button>
          ) : (
            <Button width={['100%', 0.8]} onPress={handleServiceClick}>
              {cta}
            </Button>
          )}
        </Box>
      </Flex>
    </Flex>
  );
};

export default ServiceCard;
